import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Pill, WeatherBadge, timeAgo } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Search, Mail, MessageSquare, HardDrive, Gavel, ExternalLink, Heart, Meh, Frown, Sparkles, Phone,
} from "lucide-react";

const SENTIMENT = {
  happy: { icon: Heart, color: "#006747", bg: "#E3F0EA", label: "Happy" },
  neutral: { icon: Meh, color: "#1D57A5", bg: "#E1ECF7", label: "Neutral" },
  frustrated: { icon: Frown, color: "#B91C1C", bg: "#FBE0E0", label: "Frustrated" },
};

export default function ClientSpotlight() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    const { data } = await api.get("/spotlight/search", { params: { q } });
    setResults(data);
    if (data.length === 0) toast.info("No matches — try a name, email, mark, or serial number.");
  };

  const open = async (cid) => {
    setLoading(true); setDetail(null);
    try {
      const { data } = await api.get(`/spotlight/${cid}`);
      setDetail(data);
      // Load the AI summary lazily so the 360° view renders immediately.
      api.get(`/spotlight/${cid}/ai`)
        .then(({ data: aid }) => setDetail((prev) => (prev && prev.contact.id === data.contact.id ? { ...prev, ai: aid.ai } : prev)))
        .catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 md:p-10">
      <PageHeader title="Client Spotlight" subtitle="A 360° lookup across HubSpot, Gmail, Slack, Drive & TM TKO — with an AI holistic summary and call-prep notes." />

      <form onSubmit={search} className="flex gap-2 max-w-xl mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#101820]/40" />
          <Input data-testid="spotlight-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Client name, email, trademark, or serial number…" className="pl-9 h-11 bg-white border-[#BDC5DB]" />
        </div>
        <Button data-testid="spotlight-search-btn" type="submit" className="h-11 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">Search</Button>
      </form>

      {results.length > 0 && !detail && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
          {results.map((c) => (
            <button key={c.id} data-testid={`spotlight-result-${c.id}`} onClick={() => open(c.id)}
              className="text-left bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift">
              <div className="flex items-center gap-3">
                <Avatar name={c.name} size={40} />
                <div>
                  <div className="font-bold text-[#141B4D]">{c.name}</div>
                  <div className="text-xs text-[#101820]/55">{c.company}</div>
                </div>
              </div>
              <div className="mt-2 text-sm"><span className="text-[#101820]/50">Mark:</span> <span className="font-semibold text-[#141B4D]">{c.mark}</span></div>
            </button>
          ))}
        </div>
      )}

      {loading && <div className="bg-white rounded-xl p-12 text-center shadow-sm text-[#141B4D] font-semibold animate-pulse">Pulling the 360° picture and prepping your call notes…</div>}

      {detail && <Detail detail={detail} onBack={() => setDetail(null)} />}
    </div>
  );
}

function Detail({ detail, onBack }) {
  const { contact, matters, deals, sources, ai, portal } = detail;
  const primary = matters.find((m) => m.pipeline === "trademark_process") || matters[0];
  const s = (ai && SENTIMENT[ai.sentiment]) || SENTIMENT.neutral;
  const SIcon = s.icon;

  return (
    <div className="brand-fade">
      <button onClick={onBack} data-testid="spotlight-back" className="text-sm text-[#1D57A5] mb-4 hover:underline">← Back to results</button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Header card — client identity */}
          <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#1D57A5]">
            <div className="flex items-center gap-4">
              <Avatar name={contact.name} size={56} />
              <div>
                <h2 className="text-2xl font-bold text-[#141B4D]">{contact.name}</h2>
                <div className="text-[#101820]/60">{[contact.company, contact.email, contact.phone].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm">
              <Meta label="Trademarks" value={matters.length ? `${matters.length} on file` : null} />
              <Meta label="Use basis" value={contact.use_basis} />
              <Meta label="Goods/services" value={contact.goods_services} />
              <Meta label="Sales" value={deals.map((d) => `${d.plan} (${d.stage})`).join(", ")} />
            </div>
          </div>

          {/* Trademarks — one card per mark, each with its own status */}
          <div data-testid="trademark-list" className="space-y-3">
            <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 flex items-center gap-1.5"><Gavel className="w-3.5 h-3.5" /> Trademarks ({matters.length})</div>
            {matters.length === 0 && <div className="bg-white rounded-xl p-5 shadow-sm text-sm text-[#101820]/40">No matters on file.</div>}
            {matters.map((m) => {
              const tk = m.tmtko;
              return (
                <div key={m.id} data-testid={`trademark-card-${m.id}`} className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-bold text-[#141B4D] text-lg">{m.mark || contact.mark}</div>
                      <div className="text-xs text-[#1D57A5] font-semibold mt-0.5">{tk?.status || m.stage}</div>
                    </div>
                    <WeatherBadge value={m.weather} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                    <Meta label="Pipeline stage" value={m.stage} />
                    <Meta label="Serial" value={m.serial_number} mono />
                    <Meta label="Registration" value={m.registration_number || (tk?.registration_number)} mono />
                    <Meta label="Class" value={tk?.classes?.join(", ")} />
                    <Meta label="Filed" value={tk?.filing_date || (m.date_submitted || "").slice(0, 10)} />
                    <Meta label="Owner" value={m.owner?.split("@")[0]} />
                    <Meta label="Next USPTO deadline" value={tk?.next_deadline && tk.next_deadline !== "—" ? tk.next_deadline : (m.office_action_due || m.sou_due)} />
                  </div>
                  {tk?.source && <div className="text-[11px] text-[#101820]/40 mt-2 flex items-center gap-1">{tk.source}</div>}
                </div>
              );
            })}
          </div>


          {/* AI summary */}
          <div className="bg-white rounded-xl shadow-sm border border-black/5 overflow-hidden">
            <div className="section-band px-5 py-4 flex items-center justify-between">
              <span className="font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FFD100]" /> AI Holistic Summary</span>
              {ai && <Pill tone={ai.ai ? "green" : "pale"}>{ai.ai ? "Claude Sonnet 4.6" : "Fallback"}</Pill>}
            </div>
            {!ai ? (
              <div data-testid="ai-summary-loading" className="p-8 text-center text-[#141B4D] font-semibold animate-pulse">Reading the file and prepping your call notes…</div>
            ) : (
            <div className="p-5">
              <p className="text-[15px] text-[#101820] leading-relaxed">{ai.summary}</p>
              <div className="mt-4 flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[#101820]/50">Sentiment read:</span>
                <span data-testid="sentiment-badge" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold" style={{ background: s.bg, color: s.color }}>
                  <SIcon className="w-4 h-4" /> {s.label}
                </span>
                <span className="text-xs text-[#101820]/50">{ai.sentiment_reason}</span>
              </div>
              {ai.outstanding?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1.5">What's outstanding</div>
                  <ul className="list-disc list-inside text-sm text-[#101820]/80 space-y-1">
                    {ai.outstanding.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
              )}
              <div className="mt-4 callout rounded-lg p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-[#141B4D] mb-1.5 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Call-prep notes</div>
                <ul className="list-disc list-inside text-sm text-[#101820] space-y-1">
                  {(ai.call_prep || []).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Right column: sources + portal */}
        <div className="space-y-4">
          <div className={`rounded-xl p-4 shadow-sm border ${portal ? "bg-white border-[#006747]/30" : "bg-white border-[#B45309]/30"}`}>
            <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2">Client Portal</div>
            {portal ? (
              <a href={portal} target="_blank" rel="noreferrer" data-testid="portal-link"
                className="inline-flex items-center gap-2 text-[#1D57A5] font-semibold hover:underline">
                Open client portal <ExternalLink className="w-4 h-4" />
              </a>
            ) : <div className="text-sm text-[#B45309] font-semibold">No portal yet</div>}
          </div>

          <SourceBlock icon={Mail} title="Gmail" tone="yellow">
            {sources.last_contact && <div className="text-xs text-[#101820]/50 mb-2">Last contact {timeAgo(sources.last_contact)}</div>}
            {sources.gmail.length === 0 && <div className="text-sm text-[#101820]/40">No recent email in hello@.</div>}
            {sources.gmail.map((g, i) => (
              <a key={i} href={g.link} target="_blank" rel="noreferrer" className="block mb-2 hover:underline">
                <div className="text-sm font-medium text-[#141B4D]">{g.subject}</div>
                <div className="text-xs text-[#101820]/50">{g.snippet}</div>
              </a>
            ))}
            {sources.open_promises?.length > 0 && (
              <div className="mt-2 text-xs"><span className="font-bold text-[#B45309]">Open promises:</span> {sources.open_promises.join("; ")}</div>
            )}
          </SourceBlock>

          <SourceBlock icon={MessageSquare} title="Slack" tone="cobalt">
            {sources.slack.length === 0 && <div className="text-sm text-[#101820]/40">No recent mentions.</div>}
            {sources.slack.map((sl, i) => (
              <div key={i} className="mb-2 text-sm"><span className="font-semibold text-[#1D57A5]">{sl.channel}</span> · {sl.text}</div>
            ))}
          </SourceBlock>

          <SourceBlock icon={HardDrive} title="Google Drive" tone="pale">
            {(!sources.drive_master || sources.drive_master.length === 0) && sources.drive.length === 0 && (
              <div className="text-sm text-[#101820]/40">No client Drive folder linked.</div>
            )}
            {sources.drive_master?.map((mf, i) => (
              <div key={mf.id || i} data-testid={`drive-master-${i}`} className="mb-3 last:mb-0">
                <a href={mf.link} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-sm font-bold text-[#1D57A5] hover:underline">
                  <HardDrive className="w-3.5 h-3.5" /> {mf.name}
                  <span className="text-[10px] font-semibold text-[#101820]/40">— master folder</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <div className="mt-1 pl-5 space-y-0.5">
                  {mf.folders?.map((f, j) => (
                    <a key={`f${j}`} href={f.link} target="_blank" rel="noreferrer"
                      className="block text-sm text-[#141B4D] hover:underline">📁 {f.name}</a>
                  ))}
                  {mf.files?.slice(0, 8).map((f, j) => (
                    <a key={`d${j}`} href={f.link} target="_blank" rel="noreferrer"
                      className="block text-sm text-[#1D57A5] hover:underline">{f.name}</a>
                  ))}
                  {mf.folders?.length === 0 && mf.files?.length === 0 && (
                    <div className="text-xs text-[#101820]/40">Empty folder.</div>
                  )}
                </div>
              </div>
            ))}
            {(!sources.drive_master || sources.drive_master.length === 0) && sources.drive.map((d, i) => (
              <a key={i} href={d.link} target="_blank" rel="noreferrer" className="block text-sm text-[#1D57A5] hover:underline mb-1">{d.name}</a>
            ))}
          </SourceBlock>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#101820]/40">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-[#141B4D] mt-0.5`}>{value}</div>
    </div>
  );
}

function SourceBlock({ icon: Icon, title, tone, children }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-black/5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#1D57A5]" />
        <span className="font-bold text-[#141B4D] text-sm">{title}</span>
        <Pill tone={tone} className="ml-auto">source</Pill>
      </div>
      {children}
    </div>
  );
}
